import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { resolveAt } from '../../pricing/resolveAt';
import { microsToDollars } from '../../pricing/money';
import { normalizeAmount } from '../../catalog/readSheet';
import { unscaleDecimal } from '../../catalog/planWrites';
import { isCostKind } from '../../catalog/costDraft';
import { formatDollars } from './format';
import { usedByCount, groupByCategory } from './categories';

/**
 * The demoted multi-quarter grid — today's component x quarter table, moved
 * out of `CatalogPage` and given the same category grouping and in-place
 * editing `ImpactView` (the default tab) has. Where `ImpactView` shows one
 * quarter with a live ripple, this is the wide view: every publish quarter,
 * side by side, for a PM scanning a whole component's history at once.
 */

/** The quarter immediately before `quarter`, in the same 'YYYY-QN' shape
 *  `resolveAt`/`compareQuarters` use. General decrement (wraps Q1 -> the
 *  prior year's Q4) rather than an index into `publishQuarters`'s own list,
 *  so a gap in that list (were one ever to appear) cannot skip a real
 *  predecessor quarter silently. */
function previousQuarter(quarter) {
  const [yearStr, qStr] = quarter.split('-Q');
  let year = Number(yearStr);
  let q = Number(qStr) - 1;
  if (q < 1) { q = 4; year -= 1; }
  return `${year}-Q${q}`;
}

// 'carried''s wording must never contain the substring "authored" — the two
// states are asserted as mutually exclusive (CatalogPage.test.jsx:100).
const STATE_TITLE = {
  authored: 'Authored: entered directly this quarter.',
  carried: 'Carried forward from an earlier quarter.',
  reasserted: 'Re-asserted: this price was recorded again unchanged, not a new price.',
};

/**
 * `Resolution.source` (`resolveAt.ts:32`) only ever yields `'authored' |
 * 'carried'` — there is no third value anywhere in the pricing engine. A
 * re-assertion is page logic that exists nowhere else: an 'authored' cell
 * whose value is UNCHANGED from what the timeline already resolved to one
 * quarter earlier (its predecessor) reads as "I checked, still flat" rather
 * than an ordinary edit, so it needs its own label. A cell with no
 * predecessor at all (the component's first-ever authoring) is never a
 * re-assertion — there is nothing to compare against.
 */
function resolveCellState(components, sku, quarter) {
  const resolution = resolveAt(components, sku, quarter);
  if (!resolution) {
    return { text: '—', title: undefined, editText: null };
  }

  const text = formatDollars(microsToDollars(resolution.row.value));
  // What the EDITOR is pre-filled with, as distinct from what the cell DISPLAYS.
  // The grid shows two decimals because that is what a price is; the editor has
  // to round-trip the stored value exactly. `unscaleDecimal(_, 6)` is the exact
  // inverse of the `scaleDecimal(_, 6)` planWrites applies — see openEditor.
  const editText = unscaleDecimal(resolution.row.value, 6);

  if (resolution.source === 'carried') {
    return { text, title: STATE_TITLE.carried, editText };
  }

  const predecessor = resolveAt(components, sku, previousQuarter(quarter));
  const isReassertion = predecessor !== null
    && Number(predecessor.row.value) === Number(resolution.row.value);

  return {
    text,
    title: isReassertion ? STATE_TITLE.reasserted : STATE_TITLE.authored,
    editText,
  };
}

export function GridView({
  vendor, user, seed, components, categories, editableCategories, quarters, stage, busy,
}) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editing, setEditing] = useState(null);   // { sku, quarter } | null
  const [editValue, setEditValue] = useState('');
  const [filter, setFilter] = useState('');

  const toggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * Free-text narrowing on componentSku, restoring the merge-base
   * `CatalogPage`'s filter box (`git show 80559012:src/admin/catalog/
   * CatalogPage.jsx`) that Task 6's plan dropped assuming the views would
   * grow their own (whole-branch review finding F3) — with the real AIC seed
   * running 30 near-identical `CMP-BASE-VCH-5000-J108-*` SKUs into a single
   * Uncategorised bucket, there was no way to find one.
   *
   * Applied to `components` BEFORE either `groupByCategory` or the cost-kind
   * filter below, so both compose into one visible-row list: a bucket's
   * header count and whether it renders at all come out of `groupByCategory`
   * for free (a category with no matching members empties out and is
   * omitted by its own "empty bucket omitted" rule, Uncategorised included),
   * and `visibleSkus`/`gridRows` never see a filtered-out SKU to begin with.
   */
  const filteredComponents = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return components;
    return (components ?? []).filter((c) => c.componentSku.toLowerCase().includes(needle));
  }, [components, filter]);

  /**
   * COST rows only — the same `COST_KINDS` set `planWrites` refuses to write
   * outside of (`costDraft.ts`), so the grid cannot show a cell the write path
   * would block. Mirrors `ImpactView.jsx`'s own copy of this filter (finding
   * C1: a percentage kind rendered as an ordinary editable `$0.00` cell); the
   * duplication between the two views' edit lifecycles is a deliberate,
   * ruled-on decision (SDD ledger, Task 5 pre-flight ruling), not something
   * Task 6 was ever going to fold away. Built off `filteredComponents`, not
   * `components`, so the free-text filter above composes with this one.
   */
  const visibleSkus = useMemo(
    () => [...new Set(
      (filteredComponents ?? []).filter((c) => isCostKind(c.kind)).map((c) => c.componentSku),
    )].sort(),
    [filteredComponents],
  );

  // `resolveCellState` is an O(total rows) `resolveAt` scan per cell (two
  // scans, for an authored one). Un-memoized, EVERY keystroke in the edit
  // input below re-renders this view and re-runs that scan for every visible
  // cell — up to `visibleSkus.length * quarters.length` — even though typing
  // an amount changes none of them. Memoized on the three inputs that
  // actually change what a cell shows; `filter` only changes which SKUs are
  // visible, already captured by `visibleSkus` itself, and `editing`/
  // `editValue`/`busy` never belong in this key.
  const gridRows = useMemo(
    () => visibleSkus.map((sku) => ({
      sku,
      cells: quarters.map((quarter) => ({
        quarter,
        state: resolveCellState(components, sku, quarter),
      })),
    })),
    [visibleSkus, quarters, components],
  );

  const groups = useMemo(
    () => groupByCategory(filteredComponents, categories, seed),
    [filteredComponents, categories, seed],
  );
  const rowsBySku = useMemo(
    () => new Map(gridRows.map((r) => [r.sku, r])), [gridRows],
  );

  // Finding F5: "focus returns to the cell" (spec) — the cell that OPENED the
  // editor, restored once the editor actually closes (Escape or Enter).
  // `cellRefs` is populated by the display cell's own ref callback below, so
  // the node handed to `.focus()` is whatever most recently rendered at that
  // key — never a stale reference to a node React already replaced. The
  // effect fires on `editing` transitions (post-commit, after the display
  // `<td>` — `role="button"`, `tabIndex={0}` — has replaced the `<input>`
  // and can actually receive focus), not synchronously inside closeEditor,
  // which would try to focus a node that still has the OLD render's
  // attributes.
  const cellRefs = useRef(new Map());
  const lastEditedKey = useRef(null);

  useEffect(() => {
    if (editing || !lastEditedKey.current) return;
    cellRefs.current.get(lastEditedKey.current)?.focus();
    lastEditedKey.current = null;
  }, [editing]);

  const openEditor = (sku, quarter, editText) => {
    lastEditedKey.current = `${sku}#${quarter}`;
    setEditing({ sku, quarter });
    setEditValue(editText ?? '');
  };
  const closeEditor = () => { setEditing(null); setEditValue(''); };

  const commitEdit = () => {
    const amount = normalizeAmount(editValue);
    if (amount === null) return;
    stage({
      vendor,
      sourceLabel: `Manual edit by ${user?.username ?? 'unknown'}`,
      cells: [{ ref: editing.sku, target: null, quarter: editing.quarter, amount }],
    });
    closeEditor();
  };

  /** Enter/Space activate a cell the way a click does. Hand-rolled on the cell
   *  rather than nesting a real <button>, exactly as CatalogPage did and for
   *  the same reasons: a click fired at an ancestor never reaches a
   *  descendant's listener, and jsdom does not implement a native button's
   *  Enter/Space activation. */
  const onCellKeyDown = (event, sku, quarter, editText) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    openEditor(sku, quarter, editText);
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); commitEdit(); }
    if (event.key === 'Escape') { event.preventDefault(); closeEditor(); }
  };

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="catalog-filter" className="block text-sm text-gray-400 mb-1">
          Filter by component SKU
        </label>
        <input
          id="catalog-filter"
          type="text"
          aria-label="Filter components"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="text-sm" aria-label="Component costs by quarter">
          <thead className="text-gray-400 text-left">
            <tr>
              <th scope="col">Component</th>
              {quarters.map((q) => <th scope="col" key={q}>{q}</th>)}
              <th scope="col">Used by</th>
            </tr>
          </thead>
          {groups.map((group) => {
            const open = !collapsed.has(group.id);
            const editable = (editableCategories ?? []).includes(group.id);
            return (
              <tbody key={group.id}>
                <tr>
                  <th scope="colgroup" colSpan={2 + quarters.length} className="text-left pt-3">
                    <button
                      type="button" onClick={() => toggle(group.id)} aria-expanded={open}
                      className="text-orange-400 text-xs tracking-wide uppercase"
                    >
                      {open ? '▾' : '▸'} {group.label} · {group.skus.length}
                    </button>
                  </th>
                </tr>
                {open && group.skus.map((sku) => (
                  <tr key={sku} className="border-t border-gray-800">
                    <th scope="row" className="font-normal">{sku}</th>
                    {(rowsBySku.get(sku)?.cells ?? []).map(({ quarter, state }) => {
                      const isEditing = editing?.sku === sku && editing?.quarter === quarter;
                      const label = `Edit cost for ${sku} ${quarter}`;
                      const cellKey = `${sku}#${quarter}`;
                      return isEditing ? (
                        <td key={cellKey}>
                          <input
                            autoFocus aria-label={label} value={editValue} disabled={busy}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={onInputKeyDown}
                            className="bg-gray-900 border border-orange-400 rounded px-1 w-28 text-right"
                          />
                        </td>
                      ) : editable ? (
                        <td
                          key={cellKey}
                          ref={(el) => {
                            if (el) cellRefs.current.set(cellKey, el);
                            else cellRefs.current.delete(cellKey);
                          }}
                          title={state.title}
                          role="button"
                          tabIndex={0}
                          aria-label={label}
                          onClick={() => openEditor(sku, quarter, state.editText)}
                          onKeyDown={(e) => onCellKeyDown(e, sku, quarter, state.editText)}
                          className="cursor-pointer px-2"
                        >
                          {state.text}
                        </td>
                      ) : (
                        <td
                          key={cellKey}
                          title={state.title}
                          aria-label={`${sku} ${quarter} cost`}
                          className="px-2 text-gray-500"
                        >
                          {state.text}
                        </td>
                      );
                    })}
                    <td aria-label={`${sku} used by`} className="px-2 text-right text-gray-400">
                      {usedByCount(seed, sku)}
                    </td>
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
    </div>
  );
}

export default GridView;
